import type { ClinicianPublicationStatus } from "@/api/publication";
import { formatDateTime } from "@/lib/format";

/**
 * The durable clinician acceptance, said out loud (#436).
 *
 * #431 made acceptance a SECOND server axis, reported on every `state`:
 * `candidateAcceptance` plus the all-or-nothing bundle
 * `acceptedCandidateId`/`acceptedBy`/`acceptedAt`. `PublicationLane` already
 * obeyed it — it is what decides which button is offered — but nothing showed
 * it, so a clinician who accepted a version and came back after a reload, a
 * publication request or a failed automated review saw a lane that behaved
 * correctly and said nothing about why. "Already accepted" and "the button is
 * missing" looked identical.
 *
 * SECONDARY, NEVER A SECOND STATUS. `PublicationStatusView` keeps the badge and
 * the one sentence the compact `state` owns, including the `action_needed`
 * recovery that deliberately REPLACES the progress reading; this renders
 * beneath it as detail. No badge, no heading, no card — #203 bans a second
 * publication surface and `invariants/projectUxAmendment.test.ts` fails the
 * build if one appears.
 *
 * IT NEVER INFERS. Acceptance is read from the projection and nowhere else: not
 * from `state === "accepted"` (which a publication row outranks, which is the
 * whole defect), not from a mutation result, not from page-local memory. When
 * the projection says nothing was accepted, this renders nothing.
 */

/**
 * How the reported acceptance stands against the version on screen.
 *
 * `current` is the only reading that may present as an acceptance OF this
 * version, and it requires the server's own discriminator AND identity
 * equality with the candidate the lane is bound to — the same fail-closed pair
 * that gates the publication control. `superseded` is the server saying a newer
 * version was frozen after the acceptance. `other_version` is the fail-closed
 * remainder: a payload claiming `accepted` while naming some other candidate,
 * or a discriminator this build does not recognise. Neither of the last two may
 * read as a current acceptance, and neither may vanish — a bare "nothing
 * accepted" would erase a clinical fact the server still reports.
 */
type AcceptanceStanding = "current" | "superseded" | "other_version";

export function CandidateAcceptanceDetail({
  status,
  currentCandidateId = null,
  viewerSubject = null,
}: {
  status: ClinicianPublicationStatus;
  /** `ProjectOverview.draft.latestCandidate`'s id — the version on screen. */
  currentCandidateId?: string | null;
  /**
   * The signed-in principal's subject, for self-attribution only.
   *
   * `acceptedBy` is an immutable Entra subject and no directory in this
   * application resolves one to a name (`api/roleAssignments.ts` rejects
   * display-form subjects outright). So the only attribution that can be made
   * truthfully is "was this me": everyone else is "another clinician", and the
   * raw subject is never promoted into the clinician's sentence.
   */
  viewerSubject?: string | null;
}) {
  const { candidateAcceptance, acceptedCandidateId, acceptedBy, acceptedAt } =
    status;

  if (candidateAcceptance === "none") {
    return null;
  }

  // The bundle is all-or-nothing server-side, enforced by a model validator.
  // Generated types describe that contract; they do not validate a response,
  // and half an acceptance is not an acceptance — rendering "accepted by
  // undefined" or an em dash where the instant belongs would put a fabricated
  // clinical record on the screen. Silence is the fail-closed reading, and the
  // control gate below is unaffected by it either way.
  if (!acceptedCandidateId || !acceptedBy || !acceptedAt) {
    return null;
  }

  const standing: AcceptanceStanding =
    candidateAcceptance === "accepted" &&
    currentCandidateId !== null &&
    acceptedCandidateId === currentCandidateId
      ? "current"
      : candidateAcceptance === "superseded_by_newer_version"
        ? "superseded"
        : "other_version";

  const mine = viewerSubject !== null && acceptedBy === viewerSubject;
  const when = formatDateTime(acceptedAt);

  /**
   * Both stale readings lead with the same deterministic marker — the current
   * version is not accepted — and only then attribute the historical fact, so
   * the standing is legible before the history is read.
   *
   * They say nothing about the publication review that
   * `signoff_no_longer_current` retires. That is the MANAGER's fact, on the
   * other axis, and the contract warns that browser copy must not turn it into
   * an instruction to accept again. The superseded sentence names the one
   * cause the server actually claims: a newer version was frozen.
   */
  const stale = `The current version has not been accepted yet. ${mine ? "You" : "Another clinician"} accepted`;

  return (
    <p className="text-sm text-muted-foreground">
      {standing === "current"
        ? `This version was accepted by ${mine ? "you" : "another clinician"} on ${when}.`
        : standing === "superseded"
          ? `${stale} an earlier version of this Project on ${when}, and a newer version has since been frozen.`
          : `${stale} a different version of this Project on ${when}.`}
    </p>
  );
}
